/**
 * IndicatorRow Component
 *
 * Displays a single indicator with a mini gauge showing the score.
 * Used inside expanded LinearGauge components.
 */

import type { SankeyIndicator } from '../../types';
import { getScoreColor } from '../../utils';
import { translateIndicator } from '../../../../config/reportTranslations';
import styles from './IndicatorRow.module.css';

interface IndicatorRowProps {
  indicator: SankeyIndicator;
}

export default function IndicatorRow({ indicator }: IndicatorRowProps) {
  const score = parseFloat(indicator.indicator_score);
  const value = parseFloat(indicator.indicator_value);
  const color = getScoreColor(score, 100);
  // Use local translation map as fallback when DB name is missing (returns indicator_id as name)
  const displayName = indicator.indicator_name !== indicator.indicator_id
    ? indicator.indicator_name
    : translateIndicator(indicator.indicator_id);

  // Format value as percentage if it's a ratio
  const formattedValue = value < 10 ? `${(value * 100).toFixed(2)}%` : value.toFixed(2);

  return (
    <div className={styles.indicatorRow}>
      <div className={styles.indicatorInfo}>
        <span className={styles.indicatorName}>{displayName}</span>
        <span className={styles.indicatorActual}>{formattedValue}</span>
      </div>
      <div className={styles.indicatorGauge}>
        <div className={styles.miniTrackContainer}>
          <div className={styles.miniTrack}>
            <div
              className={styles.miniFill}
              style={{
                width: `${Math.min(Math.max(score, 0), 100)}%`,
                backgroundColor: `${color}44`,
              }}
            />
            <div
              className={styles.miniTriangleMarker}
              style={{ left: `${Math.min(Math.max(score, 2), 98)}%` }}
            >
              <svg width="8" height="5" viewBox="0 0 8 5">
                <polygon points="4,5 8,0 0,0" fill={color} />
              </svg>
            </div>
          </div>
          <span className={styles.miniScore} style={{ color }}>
            {score.toFixed(0)}
          </span>
        </div>
      </div>
    </div>
  );
}
